import { Grid } from '@mui/material'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import { Modal } from 'components/core-ui/Modal'
import {
  getCurrentLocationInfo,
  getExternalArticlesReport,
  getOperationTypesOptions,
  getWasteCategoryOptions
} from 'components/waste-management/api'
import { useNotification } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { getUserStoreInfo } from 'utils/authService'
import { useFormik } from 'formik'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { handleQueryError } from 'utils/helpers'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { Button } from '@fullscreendigital/carrefour-dex-fe'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { DangerousDEEEReportForm } from 'components/waste-management/common/DangerousDEEEReportForm'
import { NonDangerousDEEEReportForm } from 'components/waste-management/common/NonDangerousDEEEReportForm'
import { getInitialValues, getValidationSchema } from 'components/waste-management/common/helpers'
import {
  DangerousDEEEPayloadItem,
  EditCreateModalProps,
  NonDangerousDEEEPayloadItem
} from 'components/waste-management/interfaces'
import { toWasteReportPayload } from 'components/waste-management/common/serializers'
import useExpeditorAuthorizationData from 'components/waste-management/common/hooks/useExpeditorAuthorizationData'

const ExternalArticlesReportModal = (props: EditCreateModalProps) => {
  const { isModalOpen, closeModal, isEdit, editItemId, shouldRefetchItem, setShouldRefreshData } =
    props

  const [currentStore, setCurrentStore] = useState(null)
  const [isDangerousDEEE, setIsDangerousDEEE] = useState(false)

  useEffect(() => {
    setCurrentStore(getUserStoreInfo())
  }, [])

  const { showNotification } = useNotification()

  const { getAuthorizationData, setAuthorizationData } = useExpeditorAuthorizationData()

  const expeditorAuthorizationData = useMemo(() => {
    if (isModalOpen) {
      return getAuthorizationData()
    } else {
      return {
        expeditorEnvironmentAuthorizationNumber: '',
        expeditorEnvironmentAuthorizationExpirationDate: ''
      }
    }
  }, [isModalOpen])

  const { data: editItem } = getExternalArticlesReport(
    showNotification,
    editItemId,
    shouldRefetchItem
  )

  const { data: operationTypesOptions } = getOperationTypesOptions(showNotification)

  const initialValues = useMemo(
    () => getInitialValues(editItem, false),
    [editItem, expeditorAuthorizationData]
  )

  const validationSchema = useMemo(
    () => getValidationSchema(isDangerousDEEE, true),
    [editItem, isDangerousDEEE]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(
        toWasteReportPayload(
          {
            ...values,
            measuredWeight: +values.measuredWeight,
            carrierHandoverDate: new Date(
              typeof values.carrierHandoverDate === 'string'
                ? values.carrierHandoverDate
                : values.carrierHandoverDate.toDate()
            ),
            downloadDate: values.downloadDate ? new Date(values.downloadDate) : null,
            carrierReceivedWeight: values.carrierReceivedWeight
              ? +values.carrierReceivedWeight
              : null,
            companyId: Number(values.companyId)
          },
          isDangerousDEEE,
          true
        )
      )

      if (values.expeditorEnvironmentAuthorizationNumber !== undefined) {
        setAuthorizationData({
          expeditorEnvironmentAuthorizationNumber: values.expeditorEnvironmentAuthorizationNumber,
          expeditorEnvironmentAuthorizationExpirationDate:
            values.expeditorEnvironmentAuthorizationExpirationDate
        })
      }
    }
  })

  useEffect(() => {
    const hazardous = formik.values.wasteCategory?.[0]?.wasteReportCollectionType === 'HAZARDOUS'
    setIsDangerousDEEE(hazardous)
    formik.setFieldValue('wasteType', hazardous ? 'HAZARDOUS' : 'NONHAZARDOUS')
  }, [formik.values.wasteCategory])

  const { data: wasteCategoryOptions } = getWasteCategoryOptions(showNotification)

  const selectedWasteCategory = formik.values.wasteCategory?.[0]
  const selectedWasteCategoryType = selectedWasteCategory?.wasteReportCollectionType

  const filteredWasteCategoryOptions = useMemo(() => {
    const options = wasteCategoryOptions || []

    if (!selectedWasteCategory) {
      return options
    }

    if (selectedWasteCategory.code === 'AOA') {
      return options.filter(option => option.code === 'AOA')
    }

    return options.filter(option => {
      if (option.code === 'AOA') {
        return false
      }

      if (!selectedWasteCategoryType) {
        return true
      }

      return option.wasteReportCollectionType === selectedWasteCategoryType
    })
  }, [wasteCategoryOptions, selectedWasteCategory, selectedWasteCategoryType])

  const { data: currentLocationInfo } = getCurrentLocationInfo(showNotification)

  useEffect(() => {
    formik.setFieldValue(
      'expeditorEnvironmentAuthorizationNumber',
      expeditorAuthorizationData.expeditorEnvironmentAuthorizationNumber
    )
    formik.setFieldValue(
      'expeditorEnvironmentAuthorizationExpirationDate',
      expeditorAuthorizationData.expeditorEnvironmentAuthorizationExpirationDate
    )
  }, [expeditorAuthorizationData])

  const mutateReport = async (values: DangerousDEEEPayloadItem | NonDangerousDEEEPayloadItem) => {
    editItem
      ? await makeCorePutRequest(
          `/waste-management-system/external-articles/${editItem.id}`,
          values
        )
      : await makeCorePostRequest(`/waste-management-system/external-articles`, values)
  }

  const { isLoading, mutate } = useMutation(mutateReport, {
    onSuccess: res => {
      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: `Raportul a fost ${editItem?.id ? 'editat' : 'creat'} cu succes.`,
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(
        error,
        `A aparut o eroare la ${editItem?.id ? 'editarea raportului.' : 'adaugarea raportului.'}`,
        showNotification
      )
  })

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle={isEdit ? 'Editeaza raport produse externe' : 'Adauga raport produse externe'}
      modalWidth="50%"
    >
      <Grid container spacing={4}>
        <Grid xs={12} item>
          <FormInfoItems
            items={[
              {
                label: 'Locatie:',
                name: currentStore?.storeName,
                itemStyle: { gridTemplateColumns: '60px 1fr' }
              }
            ]}
          />
        </Grid>
        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            name="wasteCategory"
            label="Categoria (selectie multipla)"
            value={formik.values.wasteCategory}
            options={filteredWasteCategoryOptions}
            onChange={value => formik.setFieldValue('wasteCategory', value)}
            error={!!formik.errors.wasteCategory}
            helperText={formik.errors.wasteCategory as string}
            required
            disabled={isEdit}
            multiple
          />
        </Grid>
        {formik.values.wasteCategory?.[0]?.wasteReportCollectionType === 'HAZARDOUS' ? (
          <DangerousDEEEReportForm
            formik={formik}
            operationTypesOptions={operationTypesOptions}
            currentLocationInfo={currentLocationInfo}
            isExternalArticlesReport
          />
        ) : (
          <NonDangerousDEEEReportForm
            formik={formik}
            operationTypesOptions={operationTypesOptions}
            currentLocationInfo={currentLocationInfo}
            isExternalArticlesReport
          />
        )}
        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={cancelModal} />
          <Button
            variant="contained"
            type="submit"
            label="Salveaza"
            onClick={() => formik.handleSubmit()}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default ExternalArticlesReportModal
